import React, { useCallback, useMemo } from "react";
import { css } from "@linaria/core";
import { colors, spacings } from "@ui/Theme";
import t from "../../../../../../../../../../i18n";
import { Dashboard } from "@ui/Illustration/react";
import BlankStateV2 from "@ui/BlankStateV2";
import type { ManualEvent } from "../../../../../../../../../../api/useEvents";
import { getEventScope } from "../../../../../../../../../../domain/event";
import Filters, { useEventHistoryFilter } from "../../../../../components/SidePanel/Filters";
import { type ManualHistoryGroup } from "../../../../../../../../../../api/events/histories";
import HistoryContent from "../../../../../components/SidePanel/HistoryContent";
import BlankState from "@ui/BlankStateV2";

const SidePanelHistory = ({
  projectName,
  projectTimezone,
  manualEvent,
  manualFaultEventsHistory,
}: {
  projectName: string;
  projectTimezone: string;
  manualEvent: ManualEvent;
  manualFaultEventsHistory: ManualHistoryGroup[];
}) => {
  const [filterValues, setFilterValues] = useEventHistoryFilter({
    scope: getEventScope(manualEvent),
  });
  const projectTypes = useMemo(
    () => manualFaultEventsHistory.flatMap(history => (history.typeId ? [history.typeId] : [])),
    [manualFaultEventsHistory],
  );
  const projectSites = useMemo(
    () => manualFaultEventsHistory.flatMap(history => (history.siteId ? [history.siteId] : [])),
    [manualFaultEventsHistory],
  );
  const filterFaultEvents = useCallback(
    (events: ManualHistoryGroup["events"]) =>
      events.filter(fe => {
        const statusMatches =
          filterValues.eventStatus.length === 0 || filterValues.eventStatus.includes(fe.status);
        return statusMatches;
      }),
    [filterValues],
  );
  const filterByProject = useCallback(
    (history: ManualHistoryGroup) => {
      const typeMatches =
        filterValues.projectType.length === 0 ||
        (history.typeId !== null && filterValues.projectType.includes(history.typeId));
      const siteMatches =
        filterValues.projectSite.length === 0 ||
        (history.siteId !== null && filterValues.projectSite.includes(history.siteId));

      return typeMatches && siteMatches && history.events.length > 0;
    },
    [filterValues.projectType, filterValues.projectSite],
  );
  const filteredHistory = useMemo(() => {
    const hasFiltersOnFaultFpisode =
      filterValues.eventStatus.length > 0 || (filterValues.detection?.length ?? 0) > 0;

    const sortedFilteredHistory = manualFaultEventsHistory
      .map(history => {
        const filteredFaultEvents = hasFiltersOnFaultFpisode
          ? filterFaultEvents(history.events)
          : history.events;

        const sortedFaultEvents = filteredFaultEvents.sort(
          (a, b) => new Date(a.startsAt).getTime() - new Date(b.startsAt).getTime(),
        );

        return {
          ...history,
          events: sortedFaultEvents,
        };
      })
      .filter(filterByProject)
      .sort((a, b) => a.projectName.localeCompare(b.projectName));

    const projectHome = sortedFilteredHistory.find(history => history.projectName === projectName);

    if (projectHome) {
      return [
        {
          ...projectHome,
          projectName: t("project.fault_event.side_panel.history.current_project"),
        },
        ...sortedFilteredHistory.filter(history => history !== projectHome),
      ];
    }

    return sortedFilteredHistory;
  }, [manualFaultEventsHistory, filterValues, filterFaultEvents, filterByProject, projectName]);

  if (manualFaultEventsHistory.length === 0)
    return (
      <BlankStateV2
        className={emptyBlankState}
        title={t("project.event.manual_fault.side_panel.history.blank_state.title")}
        subtitle={t("project.event.manual_fault.side_panel.history.blank_state.subtitle")}
        picto={<Dashboard size="l" />}
      />
    );

  if (manualFaultEventsHistory.length === 0)
    return (
      <BlankState
        className={emptyBlankState}
        title={t("project.fault_event.side_panel.history.blank_state.title")}
        subtitle={t("project.fault_event.side_panel.history.blank_state.subtitle")}
        picto={<Dashboard size="l" />}
      />
    );

  return (
    <div className={sidePanelHistory}>
      <Filters
        className={filterWrapper}
        projectTypes={projectTypes}
        projectSites={projectSites}
        filterValues={{
          ...filterValues,
          detection: filterValues.detection ?? [],
        }}
        setFilterValues={setFilterValues}
        showDetection={false}
      />
      {filteredHistory.length === 0 ? (
        <BlankStateV2
          className={emptyBlankState}
          title={t("project.event.manual_fault.side_panel.history.blank_state.title")}
          subtitle={t("project.event.manual_fault.side_panel.history.blank_state.subtitle")}
          picto={<Dashboard size="l" />}
        />
      ) : (
        <div className={sidePanelHistory}>
          <HistoryContent histories={filteredHistory} projectTimezone={projectTimezone} />
        </div>
      )}
    </div>
  );
};
export default SidePanelHistory;

//#region CSS
const sidePanelHistory = css`
  height: 100%;
`;
const emptyBlankState = css`
  padding: ${spacings.m};
  justify-content: flex-start;
`;

const filterWrapper = css`
  position: sticky;
  top: 105px;

  z-index: 0;

  background-color: ${colors.hydrogen};

  align-items: center;
  padding: ${spacings.m};
  border-bottom: 1px solid ${colors.titanium100};
`;

//#endregion CSS
